import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { ErrorState } from '../../../design-system/list-view/states/ErrorState'
import { getEligibilityModel, listDecisions } from '../../../api/cartridgeStudio'
import { createTenantEligibilityOverlay } from '../../../api/eligibilityOverlay'
import { ApiError } from '../../../api/problem'
import { recordOverlaySeen } from '../../../lib/eligibilityOverlayRecents'
import { CanonReferencePane } from './CanonReferencePane'
import { OverlayDeltaEditor } from './OverlayDeltaEditor'
import { useOverlayCapabilities } from './useOverlayCapabilities'
import type { DecisionView, EligibilityModelView } from '../../../types/cartridgeStudio'
import type { DecisionDelta } from '../../../types/eligibilityOverlay'

/**
 * `/admin/eligibility-overlays/new` (design doc §3.2) — a tenant author picks a published/approved
 * canon `EligibilityModel` by id, reviews it read-only (`CanonReferencePane`), stages add/override/
 * remove `DecisionDelta`s (`OverlayDeltaEditor`), and submits ONE overlay-version create (AC2).
 *
 * <p><b>Disclosed gap — no canon-model picker/list.</b> The merged eligibility API exposes
 * `GET /eligibility-models/{id}` (by id) but no "list published models" endpoint, so this screen
 * asks for the base model's id directly (optionally prefilled via `?base_model_id=`) rather than
 * offering a searchable picker — filed as an IB-289 follow-up alongside the overlay-list gap (see
 * `EligibilityOverlaysListScreen`).
 *
 * <p><b>Capability gate (AC1/AC7).</b> `useOverlayCapabilities` reads `GET /me/ui-config`'s
 * `eligibility.overlay-author` component key (now registered server-side —
 * `SecurityConfigService.CAPABILITY_UI_COMPONENTS`, derived from the actor's real
 * `eligibility_overlay:X` capability token, IB-289 follow-up). NOTHING in this screen (not even
 * the base-model-id input) renders until that check resolves — no flash of authoring controls —
 * and it renders NOTHING (not disabled controls) when the actor lacks the capability, or when the
 * check itself errors (fail-closed, never default-permissive).
 */
export function OverlayAuthorScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const capabilities = useOverlayCapabilities()

  const [baseModelIdInput, setBaseModelIdInput] = useState(searchParams.get('base_model_id') ?? '')
  const [loadedModelId, setLoadedModelId] = useState<string | null>(null)
  const [model, setModel] = useState<EligibilityModelView | null>(null)
  const [decisions, setDecisions] = useState<DecisionView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [added, setAdded] = useState<DecisionDelta[]>([])
  const [overridden, setOverridden] = useState<DecisionDelta[]>([])
  const [removedKeys, setRemovedKeys] = useState<string[]>([])

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const prefilled = searchParams.get('base_model_id')
    if (prefilled) {
      void loadCanon(prefilled)
    }
    // Only ever auto-load once, from the initial query string — a later manual "Load canon" click
    // is the user's own explicit action, not re-triggered by this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function loadCanon(id: string) {
    setLoading(true)
    setLoadError(null)
    setModel(null)
    setDecisions(null)
    try {
      const [m, d] = await Promise.all([getEligibilityModel(id), listDecisions(id)])
      setModel(m)
      setDecisions(d)
      setLoadedModelId(id)
    } catch (err) {
      setLoadError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load that canon EligibilityModel.')
    } finally {
      setLoading(false)
    }
  }

  function violationsText(err: ApiError): string {
    const violations = err.problem.violations
    if (violations && violations.length > 0) {
      return violations.map((v) => v.message ?? v.code).join('; ')
    }
    return err.problem.detail ?? err.message
  }

  async function handleSubmit() {
    if (!loadedModelId) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const created = await createTenantEligibilityOverlay({
        baseModelId: loadedModelId,
        addedDecisions: added,
        overriddenDecisions: overridden,
        removedDecisionKeys: removedKeys,
      })
      recordOverlaySeen(created.id)
      navigate(`/admin/eligibility-overlays/${created.id}`)
    } catch (err) {
      // AC2 failure path — the exact server 422 detail, verbatim, never swallowed; zero client
      // state is treated as saved (the staged deltas remain editable on screen).
      setSubmitError(err instanceof ApiError ? violationsText(err) : 'Could not create the overlay.')
    } finally {
      setSubmitting(false)
    }
  }

  const canonKeys = (decisions ?? []).map((d) => d.key)
  const hasStagedContent = added.length > 0 || overridden.length > 0 || removedKeys.length > 0

  // AC1/AC7 — no flash of authoring controls before the capability check resolves.
  if (capabilities.status === 'loading') {
    return <div style={{ padding: 'var(--iw-space-5)', color: 'var(--iw-text-muted)' }}>Loading…</div>
  }

  // AC1 — hidden (not disabled) without eligibility_overlay:X; AC7 — the SAME treatment when the
  // check itself errored (fail-closed, never default-permissive).
  if (!capabilities.canAuthor) {
    return (
      <div style={{ padding: 'var(--iw-space-5)' }}>
        <InlineAlert severity={capabilities.error ? 'warning' : 'info'}>
          {capabilities.error
            ? `Couldn't confirm your access to author eligibility overlays (${capabilities.error}).`
            : 'You do not have permission to author eligibility overlays for this tenant.'}
        </InlineAlert>
      </div>
    )
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <Icon name="list-tree" size={18} />
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>New eligibility overlay (your tenant)</h2>
      </div>

      <InlineAlert severity="info">
        This authors a supplementary overlay for YOUR tenant only — canon stays platform-owned and
        read-only. Your overlay must travel through a distinct approver (maker ≠ checker) before it
        ever affects resolution.
      </InlineAlert>

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)', flex: 1, maxWidth: 420 }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
            Base canon EligibilityModel id (must be approved/published)
          </span>
          <input
            className="iw-input"
            value={baseModelIdInput}
            onChange={(e) => setBaseModelIdInput(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </label>
        <button
          type="button"
          className="iw-btn iw-btn--primary"
          disabled={!baseModelIdInput || loading}
          onClick={() => void loadCanon(baseModelIdInput)}
        >
          {loading ? 'Loading…' : 'Load canon'}
        </button>
      </div>

      {loadError ? <ErrorState message={loadError} onRetry={() => void loadCanon(baseModelIdInput)} /> : null}

      {model && decisions ? (
        <>
          <CanonReferencePane model={model} decisions={decisions} />

          <OverlayDeltaEditor
            canonKeys={canonKeys}
            added={added}
            overridden={overridden}
            removedKeys={removedKeys}
            onChange={(next) => {
              setAdded(next.added)
              setOverridden(next.overridden)
              setRemovedKeys(next.removedKeys)
            }}
          />

          {submitError ? <InlineAlert severity="error">{submitError}</InlineAlert> : null}

          <div>
            <button
              type="button"
              className="iw-btn iw-btn--primary"
              disabled={submitting || !hasStagedContent}
              onClick={() => void handleSubmit()}
            >
              {submitting ? 'Submitting…' : 'Submit overlay (creates a Draft)'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}
